import axios from 'axios';
import ProjectConfig from 'ProjectConfig';
import {
  PARAMETERS_FORMAT,
  PARAMETERS_FORMAT_KEY,
  PARAMETERS_VERSION_KEY,
  normalizeParameterList,
  planParametersSync,
  readPersistedParametersMeta,
  shouldConfirmCachedVersion,
  toMessageRows,
  versionToSend
} from 'Utils/applyVersionedParameters';
import {
  TJK_STORE,
  readParameterRows,
  readStore,
  writeParameterRows,
  writeStore
} from 'Utils/environmentDb';

const { CMS_GATEWAY, API_GATEWAY } = ProjectConfig;
const LOCAL_WIDGET_HOST = 'http://localhost:3100';

export const rewriteWidgetUrlToLocal = value => {
  if (typeof value !== 'string' || !value) return value;
  return value.replace(/https:\/\/widgets?(?:et)?(?:\.tst)?\.bilyoner\.com/g, LOCAL_WIDGET_HOST);
};

export const isLocalWidgetDevEnv = (env = ProjectConfig.env) => env === 'development';

export const CMS_MESSAGES_READY_EVENT = 'bilyoner:cms-messages-ready';

class EnvironmentManager {

  constructor () {
    this.parameters = ProjectConfig.parameters || {};
    this.messagesMap = new Map();
    this.tjkMessagesMap = new Map();
    this._setMessages(this.resolveWidgetMessageUrls(ProjectConfig.parameterList || []));
    this._setTjkMessages(ProjectConfig.tjkMessages || []);
    this.isReload = typeof sessionStorage !== 'undefined' ? sessionStorage?.getItem('isReloaded') : null;
    this.isMessagesLoaded = false;
    this.liveMessagesReady = false;
    this._tjkCmsFetched = false;
    this.tjkVersion = typeof window !== 'undefined' ? localStorage.getItem('tjkVersion') : null;
    this.parametersVersion = null;
    this.parametersFormat = null;
    this._appliedVersion = null;

    this.generateSportTypeIcons();
  }

  resolveWidgetMessageUrls = messages => {
    if (!Array.isArray(messages)) return messages;
    return isLocalWidgetDevEnv()
      ? this.applyLocalWidgetHostOverride(messages)
      : this.sanitizeStaleLocalWidgetUrls(messages);
  };

  applyLocalWidgetHostOverride = messages => {
    if (!Array.isArray(messages)) return messages;
    return messages.map(msg => {
      if (!msg?.key?.startsWith('widget_') || typeof msg.value !== 'string') return msg;
      return {
        ...msg,
        value: rewriteWidgetUrlToLocal(msg.value),
      };
    });
  };

  sanitizeStaleLocalWidgetUrls = messages => {
    if (!Array.isArray(messages)) return messages;
    return messages.map(msg => {
      if (!msg?.key?.startsWith('widget_') || typeof msg.value !== 'string') return msg;
      if (!msg.value.includes('localhost:3100')) return msg;
      return {
        ...msg,
        value: msg.value.replace(/http:\/\/localhost:3100/g, 'https://widgets.bilyoner.com'),
      };
    });
  };

  // Backend tab.widgetUrl CMS key'inden gecmez; ThemedWidgetIframe bu yoldan ezer.
  resolveWidgetEmbedUrl = url => {
    if (!isLocalWidgetDevEnv()) return url;
    return rewriteWidgetUrlToLocal(url);
  };

  _persistParametersVersion = version => {
    if (version == null || version === '') {
      return;
    }

    this.parametersVersion = String(version);
    if (typeof window !== 'undefined') {
      localStorage.setItem(PARAMETERS_VERSION_KEY, this.parametersVersion);
    }
  };

  _markParametersSnapshotReady = () => {
    this.parametersFormat = PARAMETERS_FORMAT;
    if (typeof window !== 'undefined') {
      localStorage.setItem(PARAMETERS_FORMAT_KEY, PARAMETERS_FORMAT);
    }
  };

  _stampAppliedVersion = () => {
    if (this.parametersVersion == null || this.parametersVersion === '') {
      return;
    }

    this._appliedVersion = String(this.parametersVersion);
  };

  _applyParameterRows = (rows, version = this.parametersVersion) => {
    const messageRows = toMessageRows(rows);
    if (!messageRows.length) {
      return;
    }

    const nextVersion = version == null || version === '' ? null : String(version);

    if (nextVersion != null && nextVersion === this._appliedVersion) {
      return;
    }

    this._setMessages(this.resolveWidgetMessageUrls(messageRows));
    this.parameters = normalizeParameterList(messageRows);
    this.generateSportTypeIcons();

    if (nextVersion != null) {
      this._appliedVersion = nextVersion;
    }
  };

  checkPageReload = async () => {
    if (!this.isReload) {
      if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('isReloaded', 'true');
      }
      return null;
    }

    try {
      const [tjkRows, storedRows] = await Promise.all([
        readStore(TJK_STORE),
        readParameterRows()
      ]);
      if (tjkRows.length) {
        this._setTjkMessages(tjkRows);
      }
      if (storedRows.length) {
        this._applyParameterRows(storedRows);
        this.markLiveMessagesReady();
      }
      return storedRows;
    } catch (e) {
      console.error('IndexedDB yükleme hatası:', e);
      return null;
    }
  };

  refreshMessages = async () => {
    await this._getParameters();
  };

  generateSportTypeIcons () {
    let sportTypeHash = {};

    for (const [key, value] of Object.entries(this.parameters || {})) {
      if (key.includes('ikon_sport_type_')) {
        sportTypeHash = {
          ...sportTypeHash,
          [key.replace('ikon_sport_type_', '')]: value,
        };
      }
    }
    this.sportTypeHash = sportTypeHash;
  }

  _fetchParameters = async version => {
    const query = version ? `?version=${encodeURIComponent(version)}` : '';
    const url = `${API_GATEWAY}/content/v1/parameters${query}`;
    const res = await axios.get(url);
    return res.data || {};
  };

  _getParameters = async (preloadedStoredRows = null) => {
    if (typeof window === 'undefined') {
      this.markLiveMessagesReady();
      return;
    }

    let storedRows = preloadedStoredRows;
    // checkPageReload() already ran _applyParameterRows(preloadedStoredRows) when it had rows -
    // on a cache-hit (no new list from the server) plan.rows is just storedRows again, so
    // re-applying it would rebuild messagesMap/sportTypeHash for nothing.
    const alreadyApplied = Array.isArray(preloadedStoredRows) && preloadedStoredRows.length > 0;

    try {
      const persisted = readPersistedParametersMeta(
        typeof localStorage !== 'undefined' ? localStorage : null
      );
      this.parametersVersion = persisted.version;
      this.parametersFormat = persisted.format;

      storedRows = storedRows ?? await readParameterRows();
      const data = await this._fetchParameters(
        versionToSend(storedRows, this.parametersVersion, this.parametersFormat)
      );

      const plan = planParametersSync({
        data,
        currentParameters: this.parameters,
        storedRows,
        storedVersion: this.parametersVersion,
        persistFormat: this.parametersFormat
      });

      if (plan.shouldApply && !(alreadyApplied && !plan.shouldPersist)) {
        this._applyParameterRows(plan.rows, plan.version);
      } else if (alreadyApplied) {
        this._stampAppliedVersion();
      }

      if (plan.shouldPersist) {
        try {
          await writeParameterRows(plan.rows);
          this._markParametersSnapshotReady();
          this._persistParametersVersion(plan.version);
        } catch (persistError) {
          console.error('[EnvironmentManager] parameters persist failed:', persistError);
        }
      } else if (shouldConfirmCachedVersion(plan, this.parametersVersion)) {
        this._persistParametersVersion(plan.version);
      }
    } catch (e) {
      console.error('[EnvironmentManager] parameters fetch failed:', e);
      try {
        const fallbackRows = storedRows ?? await readParameterRows();
        if (fallbackRows.length) {
          this._applyParameterRows(fallbackRows);
        }
      } catch (dbError) {
        console.error('[EnvironmentManager] parameters IndexedDB fallback failed:', dbError);
      }
    } finally {
      this.markLiveMessagesReady();
    }
  };

  _getTjkCms = async () => {
    try {
      if (typeof window !== 'undefined') {
        this.tjkVersion = localStorage.getItem('tjkVersion');
      }

      const storedTjkRows = await readStore(TJK_STORE);
      if (!storedTjkRows.length) {
        this.tjkVersion = null;
      }

      const query = this.tjkVersion ? `?version=${this.tjkVersion}` : '';
      const url = CMS_GATEWAY + `/api/tjk/content/text/web${query}`;

      const res = await axios.get(url);
      const { version, parameters } = res.data || {};

      if (version != null) {

        if (String(version) !== String(this.tjkVersion) && Array.isArray(parameters) && parameters.length) {

          this._setTjkMessages(parameters);

          try {
            await writeStore(TJK_STORE, this.tjkMessages);
            this.tjkVersion = String(version);
            if (typeof window !== 'undefined') {
              localStorage.setItem('tjkVersion', this.tjkVersion);
            }
          } catch (writeError) {
            console.error('[EnvironmentManager] tjk persist failed:', writeError);
            // don't adopt the new version on a failed write - keep sending the old one so the
            // next request re-fetches instead of getting a false cache-hit.
          }
        } else if (!this.tjkMessages?.length) {
          this._setTjkMessages(storedTjkRows);
        }
      }
    } catch (e) {
      try {
        this._setTjkMessages(await readStore(TJK_STORE));
      } catch {
        this._setTjkMessages([]);
      }
    } finally {
      this._tjkCmsFetched = true;
    }
  };

  isTjkMessagesReady () {
    return this._tjkCmsFetched;
  }

  // for...of + continue, not reduce: a mutating reduce is a side-effect in a fold, and a
  // pure reduce that copies the accumulator each row allocates a new object per CMS row
  // (~thousands, including the 5-minute poll). First-wins stays one Map, one pass.
  _indexMessageList (rows, keyMap) {
    keyMap.clear();

    if (!Array.isArray(rows)) {
      return;
    }

    for (const item of rows) {
      if (!item || item.key == null || item.key === '') {
        continue;
      }

      if (!keyMap.has(item.key)) {
        keyMap.set(item.key, item);
      }
    }
  }

  _setMessages (rows) {
    this.messages = Array.isArray(rows) ? rows : [];
    this._indexMessageList(this.messages, this.messagesMap);
  }

  _setTjkMessages (rows) {
    this.tjkMessages = Array.isArray(rows) ? rows : [];
    this._indexMessageList(this.tjkMessages, this.tjkMessagesMap);
  }

  getMainMessage (key) {
    if (key == null || key === '') {
      return undefined;
    }

    return this.messagesMap.get(key);
  }

  detectType = value => {
    if (typeof value !== 'string') {
      return value;
    }

    if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
      return value.toLowerCase() === 'true';
    } else if (!isNaN(Number(value)) && value.trim() !== '') {
      return Number(value);
    }
    return value;
  };

  getParameter (key) {
    return this.detectType(this.getMainMessage(key)?.value ?? '');
  }

  isLiveMessagesReady () {
    return this.liveMessagesReady === true;
  }

  markLiveMessagesReady () {
    this.isMessagesLoaded = true;
    this.liveMessagesReady = true;
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.dispatchEvent(new Event(CMS_MESSAGES_READY_EVENT));
    } catch (e) {
      console.error('[theme] cms-ready dispatch failed:', e);
    }
  }

  waitForMessages () {
    return new Promise(resolve => {
      if (this.isMessagesLoaded) {
        resolve();
        return;
      }

      const checkInterval = setInterval(() => {
        if (this.isMessagesLoaded) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);

      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 5000);
    });
  }

  isTjkContext () {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.includes('tjk');
  }

  // Always reads the main CMS list, regardless of /tjk route context - for callers that
  // explicitly need main CMS flags/text even while on a TJK page (getMessageText switches
  // to tjkMessages there).
  getMainMessageText (matchObject) {
    return this.getMainMessage(matchObject?.key)?.value ?? '';
  }

  getMessageText (matchObject) {
    const key = matchObject?.key;
    const map = this.isTjkContext() ? this.tjkMessagesMap : this.messagesMap;
    const message = key != null && key !== '' ? map.get(key) : undefined;

    if (this.isTjkContext() && !message && ProjectConfig.env !== 'production') {
      console.log(`cms ${matchObject.key} değeri bulunamadı`);
    }

    return message?.value ?? '';
  }
}

export default new EnvironmentManager();
